import VueRecaptcha from 'vue-recaptcha'
import FormGroup from '../vue-components/FormGroup'
import {isDebug} from '../utils/environment';

const STATE = {
    IDLE: 'IDLE',       // Form is ready
    PROCESSING: 'PROCESSING', // Form is being processed
    TESTING: 'TESTING',    // Form is being tested
    SENDING: 'SENDING',    // Form is being sent and awaiting response
    INVALID: 'INVALID',    // Form is invalid (HTTP 400)
    ERRORED: 'ERRORED',    // Form can not be processed (HTTP 500)
    COMPLETED: 'COMPLETED',  // Form was processed successfully (HTTP 201)
}

// TODO : complete
const GENERIC_MESSAGES = {
    critical: 'Une erreur s’est produite. Veuillez réessayer plus tard ou nous contacter ici',
    redirect: 'Il s’est produit une erreur prévenant la redirection du formulaire. Il est fort probable que la soumission sois quand même valide. Dans le cas contraire, veuillez réessayer plus tard ou nous contacter ici'
}

const formMixin = {
    data: () => {
        return {
            formState: STATE.IDLE,
            formFeedback: '',
            serverErrors: {},
            serverErrorMessages: null,
        }
    },

    components: {
        FormGroup,
        VueRecaptcha
    },

    computed: {
        isFormReady: function () {
            return [
                STATE.IDLE,
                STATE.ERRORED,
            ].includes(this.formState) && !this.$v.$invalid
        },
        isFormBusy: function () {
            return [
                STATE.PROCESSING,
                STATE.TESTING,
                STATE.SENDING,
            ].includes(this.formState)
        },
        isFormSending() {
            return STATE.SENDING === this.formState
        },
        formHasError: function () {
            return [
                STATE.INVALID,
                STATE.ERRORED,
            ].includes(this.formState)
        },
        formIsCompleted: function () {
            return STATE.COMPLETED === this.formState
        }
    },

    methods: {
        initForm() {
            // Set initial values
            this.moduleInstance.initialValues && this.setInitialValues(this.moduleInstance.initialValues)
        },
        setInitialValues(values) {
            Object.assign(this, values)
        },
        handleSubmit(event) {
            if (this.isFormBusy) {
                console.warn('Form is busy')
                return false
            }

            this.$v.$touch();

            // Scroll to invalid field
            if (this.$v.$invalid) {
                this.$nextTick(() => {
                    const getInvalidFields = this.$el.querySelectorAll('.c-form_item.is-invalid')
                    if (getInvalidFields.length) {
                        this.moduleInstance && this.moduleInstance.call('scrollTo', [ getInvalidFields[0], {offset: -40} ], 'Scroll')
                    }
                })
                return
            }

            event.target.checkValidity()
            this.submitEvent = event

            this.formState = STATE.PROCESSING

            try {
                const recaptcha = this.$refs.recaptcha
                if (recaptcha) {
                    this.formState = STATE.TESTING
                    // The execution will result in 'onVerify'
                    return recaptcha.execute()
                }

                isDebug && console.log('[App.Form.handleSubmit] Submitting without recaptcha…')

                this.submitForm()
            } catch (error) {
                isDebug && console.error('[App.Form.handleSubmit]', error)
                this.formState = STATE.ERRORED
                this.formFeedback = GENERIC_MESSAGES.critical
            }
        },
        onVerify(response) {
            isDebug && console.log('Verify: ' + response)
            this.submitForm()
        },
        onExpired() {
            console.log('Expired')
            this.resetRecaptcha()
        },
        submitForm() {
            if (this.isFormSending) {
                console.warn('Form is already sending')
                return false
            }

            if (!this.submitEvent) {
                throw new Error('Missing submit event object')
            }

            this.formState = STATE.SENDING

            const event = this.submitEvent
            const form = event.target
            const formUrl = form.action
            const formData = new FormData(form)
            const controller = new AbortController();

            let responseStatus = '';

            fetch(formUrl, {
                method: form.method,
                body: formData,
                signal: controller.signal
            }).then(response => {
                if (response.redirected) {
                    // handle redirect
                    this.moduleInstance.call('goTo', { 'url': response.url }, 'Load')

                    controller.abort();
                }

                responseStatus = response.status

                return response.json()
            }).then(response => {
                const {
                    errors,
                    message,
                    next
                } = response

                if (errors) {
                    // clone response errors messages (this is to keep messages regardless of validation state)
                    this.serverErrorMessages = { ...errors }
                    this.serverErrors = errors
                    // trigger validation update
                    this.$v.$touch()

                    this.formState = STATE.ERRORED
                    // Scroll to top of form
                    this.moduleInstance && this.moduleInstance.call('scrollTo', [ this.$el ], 'Scroll')
                    this.formFeedback = GENERIC_MESSAGES.critical;
                } else {
                    this.formState = STATE.COMPLETED
                }

                if (message) {
                    this.formFeedback = message
                }

                if (next) {
                    try {
                        isDebug && console.log('[App.Form.submitForm] Page substitution by a feedback url…')
                        const url = new URL(window.location.href)
                        url.pathname = next
                        this.moduleInstance && this.moduleInstance.call('refresh', { url }, 'Load')
                        return
                    } catch (e) {
                        isDebug && console.error('[App.Form.submitForm] Couldn’t transition to feedback', e)
                        this.formState = STATE.ERRORED
                        // TODO add contact url
                        this.formFeedback = GENERIC_MESSAGES.redirect
                    }
                }

                this.resetRecaptcha()
            }).catch(error => {
                console.error('[App.Form.submitForm]', error)
                this.formState = STATE.ERRORED
                // TODO add contact url
                this.formFeedback = GENERIC_MESSAGES.critical
            })
        },
        resetRecaptcha() {
            const recaptcha = this.$refs.recaptcha
            if (recaptcha) {
                recaptcha.reset()
            }
        }
    },
};

export default formMixin;
